import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

// Content stays visible without this enhancement, including while the module loads.
const targets = [
  '.sotp-trust__item',
  '.sotp-feature__intro',
  '.sotp-carousel__track > li',
  '.sotp-feature__explore',
  '.sotp-category__header',
  '.sotp-category__item',
  '.sotp-story__media',
  '.sotp-story__content',
  '.sotp-story__step',
  '.product-grid__item',
  '.sotp-footer-menu',
  '.sotp-landing__collection-card',
  '.sotp-landing__scent-card',
  '.sotp-landing__split > *',
].join(',');

function initScrollMotion() {
  if (window.Shopify?.designMode || !window.IntersectionObserver || !Element.prototype.animate) return;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const running = new Map();
  let observer;

  const cancelAnimations = () => {
    for (const animation of running.values()) animation.cancel();
    running.clear();
  };

  const observe = (scope) => {
    if (!observer || reducedMotion.matches) return;
    const candidates = [...scope.querySelectorAll(targets)];
    if (scope.matches?.(targets)) candidates.unshift(scope);
    const root = getIntersectionRoot();
    const viewportBottom = root?.getBoundingClientRect().bottom ?? innerHeight;
    for (const element of candidates) {
      if (seen.has(element)) continue;
      // Never fade out content already visible, including restored scroll positions.
      if (element.getBoundingClientRect().top < viewportBottom) seen.add(element);
      else observer.observe(element);
    }
  };

  const connect = () => {
    observer?.disconnect();
    cancelAnimations();
    if (reducedMotion.matches) return;
    observer = new IntersectionObserver((entries) => {
      let stagger = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting || seen.has(entry.target)) continue;
        const element = entry.target;
        seen.add(element);
        observer.unobserve(element);
        if (reducedMotion.matches || element.contains(document.activeElement)) continue;
        const animation = element.animate(
          [{ opacity: 0, translate: '0 18px' }, { opacity: 1, translate: '0 0' }],
          { duration: 600, delay: Math.min(stagger++, 3) * 60, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'backwards' }
        );
        running.set(element, animation);
        animation.onfinish = animation.oncancel = () => running.delete(element);
      }
    }, { root: getIntersectionRoot(), threshold: 0 });
    observe(document);
  };

  connect();
  reducedMotion.addEventListener('change', connect);
  scrollContainerMediaQuery.addEventListener('change', connect);
  window.addEventListener('pagehide', cancelAnimations);
  document.addEventListener('focusin', (event) => {
    const element = event.target.closest?.(targets);
    if (!element) return;
    seen.add(element);
    observer?.unobserve(element);
    running.get(element)?.cancel();
  });

  // Horizon replaces or appends collection cards without a full page navigation.
  const main = document.getElementById('MainContent');
  if (main) new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) if (node instanceof Element) observe(node);
    }
  }).observe(main, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initScrollMotion, { once: true });
else initScrollMotion();
